// schema
import * as ISchema from "@schema";

import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { createRefetchContainer, RelayRefetchProp } from "react-relay";

import classNames from "classnames";

import { IOrg } from "@schema";

import { AvatarRow } from "@components/avatar-row-new";

import { snackBarOpen } from "@actions/snack-bar.actions";
import { IReduxState } from "@reducers/root.reducer";

import { getDomainOffEmail } from "@clockwise/client-commons/src/util/email";
import {
  maybeGetBillingGroupsFromOrg,
  getAdminsFromBillingGroup,
  getSubscriptionFromBillingGroup,
  getStripeCustomerPortalSessionFromBillingGroup,
  isOrgPersonInBillingGroup,
  isBillingGroupTypeSSO,
  isCurrentUserInBillingGroup,
  getPersonListCountFromBillingGroup,
} from "@util/billing-group.util";
import {
  isSubscriptionProductTypeEnterprise,
  isSubscriptionProductTypeBusiness,
  isSubscriptionProductTypePro,
  getNicePlanNameForSubscription,
  getPrimaryBillingGroup,
  getOrgAccountingPeriodFromOrg,
  getFormattedCycleRangeForOrgAccountingPeriod,
  isSubscriptionNonStandard,
} from "@util/pricing.util";

import {
  adminPanelBillingGroupsPlansAndBillingFragments,
  adminPanelBillingGroupsPlansAndBillingQuery,
} from "./AdminPanelBillingGroupsPlansAndBilling.gql";
import { track, TrackingEvents } from "@src/util/analytics.util";

import {
  patchBillingGroup,
  sendBillingGroupRequestEmail,
  addManualMembersForBillingGroup,
} from "@mutations";

import { SsoAdminPortalButton } from "./sso-admin-portal-button";

// constatnts
import { paths } from "@clockwise/client-commons/src/constants/site";

import { salesContactFormUrl } from "@constants/site.constant";

import {
  Button,
  Container,
  IconButton,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import {
  ArrowForward,
  Business,
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNew,
  Email,
  PersonAdd,
} from "@material-ui/icons";
import { Title } from "@clockwise/web-commons/src/ui/admin-panel/title";

import { helpers, colors, greys, theme } from "@clockwise/web-commons/src/styles/color.styles";
import { logger } from "@src/util/logger.util";

export interface IAdminPanelBillingGroupsPlansAndBillingCmpt {
  org: IOrg;
  relay: RelayRefetchProp;
}

interface IAdminPanelBillingGroupsPlansAndBillingProps
  extends IAdminPanelBillingGroupsPlansAndBillingCmpt {
  snackBarOpen: (message: string) => void;
}

const useStyles = makeStyles({
  table: {
    tableLayout: "fixed",
    minWidth: 550,
  },
  table__head: {
    background: greys.palePaleGrey,
    "& .MuiTableCell-head": {
      fontWeight: 700,
      fontSize: 14,
      color: greys.standard,
      "& > .MuiTableSortLabel-root.MuiTableSortLabel-active": {
        color: greys.standard,
      },
    },
  },
  row__subscription: {
    width: "30%",
    "@media (min-width: 768px) and (max-width: 1620px)": {
      width: "30%",
    },
    "@media (min-width: 1621px)": {
      width: "30%",
    },
  },
  row__plan: {
    width: "15%",
    "@media (min-width: 768px)": {
      width: "15%",
    },
  },
  row__admin: {
    width: "20%",
    "@media (min-width: 768px)": {
      width: "20%",
    },
  },
  row__portal: {
    width: "20%",
    "@media (min-width: 768px) and (max-width: 1620px)": {
      width: "20%",
    },
    "@media (min-width: 1621px)": {
      width: "20%",
    },
  },
  row__expand: {
    width: "5%",
    "@media (min-width: 768px) and (max-width: 1620px)": {
      width: "5%",
    },
    "@media (min-width: 1621px)": {
      width: "5%",
    },
  },
  empty: {
    color: theme.textSecondary,
    padding: "12px 0px",
  },
  whiteBorder: {
    borderLeft: `2px solid ${greys.white}`,
  },
  blueBorder: {
    borderLeft: `2px solid ${colors.blueSky.darker}`,
  },
  settingsTableRow: {
    background: helpers.opacity(colors.blueSky.darker, 0.05),
  },
  settingsCell: {},
  settingsHeader: {
    color: greys.slate,
    fontWeight: "bold",
    fontSize: 14,
    lineHeight: "24px",
  },
  settingsBody: {
    color: greys.standard,
    fontSize: 14,
    lineHeight: "24px",
    display: "flex",
    alignItems: "center",
  },
  settingsText: {
    margin: "0 5px",
  },
  settingsSso: {
    padding: "5px 0",
  },
  link: {
    color: colors.blueCool.standard,
    textDecoration: "none",
    transition: "color .3s",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      color: colors.blueDusk.darker,
    },
  },
  underline: {
    borderBottom: `1px dashed ${greys.slate}`,
  },
  cell__row: {
    display: "flex",
    alignItems: "center",
  },
  cell__text: {
    color: greys.slate,
  },
  bold: {
    fontWeight: "bold",
  },
  userBillingGroupBadge: {
    background: colors.blueSky.darker,
    color: greys.white,
    borderRadius: 2,
    fontSize: 11,
    padding: "2px 5px",
    marginLeft: 5,
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  avatarRowDescription: {
    color: greys.slate,
    fontSize: 14,
    lineHeight: 1.5,
    marginLeft: 10,
  },
  trialContent: {
    marginTop: 20,
    fontSize: 14,
    lineHeight: "24px",
    color: greys.standard,
    display: "flex",
  },
  trialButtons: {
    marginTop: 20,
    display: "flex",
    alignItems: "center",
  },
  primaryBtn: {
    color: greys.white,
    marginRight: 15,
  },
  secondaryBtn: {
    color: greys.slate,
  },
  icon: {
    marginRight: 10,
  },
});

const AdminPanelBillingGroupsPlansAndBillingCmpt = ({
  org,
  relay,
  snackBarOpen,
}: IAdminPanelBillingGroupsPlansAndBillingProps) => {
  const classes = useStyles();

  const [expandedBillingGroupId, setExpandedBillingGroupId] = useState<string | null>(null);

  const [
    billingGroupInvitePolicy,
    setBillingGroupInvitePolicy,
  ] = useState<ISchema.InvitePolicyEnum>(ISchema.InvitePolicyEnum.AdminOnly);

  const [requestedBillingGroups, setRequestedBillingGroups] = useState<string[]>([]);
  const [joinedBillingGroups, setJoinedBillingGroups] = useState<string[]>([]);

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const billingGroupsForOrg = [...(maybeGetBillingGroupsFromOrg(org) || [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const getCurrentOrgPersons = (org: ISchema.IOrg) => {
    if (org.currentOrgPersons.__typename === "GraphEntityError") {
      logger.error("failed to render curent users from getCurrentOrgPersons");
      return [];
    }
    return org.currentOrgPersons.list;
  };

  const currentUser = getCurrentOrgPersons(org).find((person) => person.isYou);

  const inBillingGroup = useMemo(() => {
    if (!currentUser) {
      return false;
    }
    return (
      billingGroupsForOrg.some((bg) => isOrgPersonInBillingGroup(bg, currentUser)) ||
      joinedBillingGroups.length > 0
    );
  }, [currentUser, billingGroupsForOrg, joinedBillingGroups]);

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-
  const onClickViewPlans = () => {
    window.open(`/app${paths.pricing}`, "_blank");
    track(TrackingEvents.ADMIN_PANEL.PLANS_AND_BILLING_CLICK_VIEW_PLANS);
  };

  const onClickContactUs = () => {
    // track(TrackingEvents.PAYWALLS.FREEMIUM_FEED_CALLOUT_CLICK_CONTACT_US);
  };

  const handleToggleExpand = (bg: ISchema.IBillingGroup) => {
    track(TrackingEvents.ADMIN_PANEL.PLANS_AND_BILLING_EXPAND_ROW);
    if (bg.id === expandedBillingGroupId) {
      setExpandedBillingGroupId(null);
    } else {
      setExpandedBillingGroupId(bg.id);
      setBillingGroupInvitePolicy(bg.invitePolicy);
    }
  };

  const onInvitePolicyChangeSuccess = (newInvitePolicy: ISchema.InvitePolicyEnum) => {
    setBillingGroupInvitePolicy(newInvitePolicy);
  };

  const onInvitePolicyChangeFailure = () => {
    const eMsg = "Failed to set invite policy";
    logger.error(eMsg);
    snackBarOpen(eMsg);
  };

  const handleInvitePolicyChange = (
    newInvitePolicy: ISchema.InvitePolicyEnum,
    bg: ISchema.IBillingGroup,
  ) => {
    track(
      TrackingEvents.ADMIN_PANEL
        .PLANS_AND_BILLING_BILLING_GROUPS_SETTINGS_SET_ADMIN_PROVISIONING_PRIVILIGE,
    );

    patchBillingGroup(
      relay.environment,
      {
        orgRelayId: org.id,
        billingGroupId: bg.id,
        invitePolicy: newInvitePolicy,
      },
      () => onInvitePolicyChangeSuccess(newInvitePolicy),
      () => onInvitePolicyChangeFailure(),
    );
  };

  const handleBillingPortalClick = (scps: ISchema.IStripeCustomerPortalSession) => {
    track(TrackingEvents.ADMIN_PANEL.PLANS_AND_BILLING_CLICK_STRIPE_BILLING_PORTAL);
    window.open(scps.sessionUrl, "_blank");
  };

  const onRequestToJoinMember = (personId: string, billingGroupId: string) => {
    track(TrackingEvents.ADMIN_PANEL.MEMBERS_REQUEST_TO_JOIN_BILLING_GROUP, { billingGroupId });
    sendBillingGroupRequestEmail(
      relay.environment,
      {
        orgRelayId: org.id,
        billingGroupId,
        personId,
      },
      () => onRequestToJoinMemberSuccess(billingGroupId),
      onRequestToJoinMemberFailure,
    );
  };

  const onRequestToJoinMemberSuccess = (billingGroupId: string) => {
    snackBarOpen("Request sent!");
    setRequestedBillingGroups([...requestedBillingGroups, billingGroupId]);
  };

  const onRequestToJoinMemberFailure = () => {
    const errorMsg = "Request failed to send";
    logger.error(errorMsg);
    snackBarOpen(errorMsg);
  };

  const onAddMemberSuccess = (billingGroupId: string) => {
    snackBarOpen("Successfully added member!");
    setJoinedBillingGroups([...joinedBillingGroups, billingGroupId]);
  };

  const onAddMemberFailure = () => {
    const errorMsg = "Failed to add member";
    logger.error(errorMsg);
    snackBarOpen(errorMsg);
  };

  const onAddMember = (personIds: string[], billingGroupId: string) => {
    track(TrackingEvents.ADMIN_PANEL.MEMBERS_ADD_TO_BILLING_GROUP, { billingGroupId });
    addManualMembersForBillingGroup(
      relay.environment,
      {
        orgRelayId: org.id,
        billingGroupId,
        personIds: personIds,
      },
      () => onAddMemberSuccess(billingGroupId),
      () => onAddMemberFailure(),
    );
  };
  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  const getPrimaryBillingGroupID = () => {
    const primaryBillingGroup = getPrimaryBillingGroup(org);
    if (!primaryBillingGroup) {
      return null;
    }
    return primaryBillingGroup.id;
  };

  const renderBillingGroupName = (bg: ISchema.IBillingGroup) => {
    const isPrimaryBillingGroup = getPrimaryBillingGroupID() === bg.id;
    return (
      <TableCell>
        <span className={classes.bold}>{bg.name}</span>
        {isPrimaryBillingGroup && <span className={classes.userBillingGroupBadge}>Your plan</span>}
      </TableCell>
    );
  };

  const renderBillingGroupPlanTier = (bg: ISchema.IBillingGroup) => {
    const billingGroupSubscription = getSubscriptionFromBillingGroup(bg);
    const planName = billingGroupSubscription
      ? getNicePlanNameForSubscription(billingGroupSubscription.subscription)
      : "";

    return (
      <TableCell className={classes.cell__text}>{planName ? `${planName} plan` : "N/A"}</TableCell>
    );
  };

  const renderBillingGroupAdmins = (bg: ISchema.IBillingGroup) => {
    const admins = getAdminsFromBillingGroup(bg) || [];
    if (admins.length < 1) {
      return <TableCell></TableCell>;
    }

    const adminEmails = admins.map((admin) => admin.primaryCalendarId);

    const formatAdminEmails = (emails: string[]) => {
      if (emails.length === 1) {
        return emails[0];
      } else if (emails.length === 2) {
        return `${emails[0]} and ${emails[1]}`;
      } else if (emails.length > 2) {
        return [
          ...adminEmails.slice(0, adminEmails.length - 1),
          `and ${adminEmails[adminEmails.length - 1]}`,
        ].join(", ");
      } else {
        return "";
      }
    };

    return (
      <TableCell className={classes.cell__text}>
        <span>{formatAdminEmails(adminEmails)}</span>
      </TableCell>
    );
  };

  const renderBillingGroupBillingPortal = (bg: ISchema.IBillingGroup) => {
    const billingGroupSubscription = getSubscriptionFromBillingGroup(bg);

    const productTypeRequiresAdmin =
      billingGroupSubscription &&
      (isSubscriptionProductTypeEnterprise(billingGroupSubscription.subscription) ||
        isSubscriptionProductTypeBusiness(billingGroupSubscription.subscription));

    const admins = getAdminsFromBillingGroup(bg) || [];
    const isAdmin = admins.findIndex((a) => a.isYou) >= 0;

    if ((productTypeRequiresAdmin && !isAdmin) || !isCurrentUserInBillingGroup(bg)) {
      return <TableCell></TableCell>;
    }

    const stripeCustomerPortalSession = getStripeCustomerPortalSessionFromBillingGroup(bg);

    return (
      <TableCell>
        {stripeCustomerPortalSession ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<OpenInNew fill={greys.white} />}
            onClick={() => handleBillingPortalClick(stripeCustomerPortalSession)}
          >
            Billing Portal
          </Button>
        ) : (
          ""
        )}
      </TableCell>
    );
  };

  const renderBillingGroupRequestToJoin = (bg: ISchema.IBillingGroup) => {
    const isFreeUserAndCanAddSelf = bg.invitePolicy === ISchema.InvitePolicyEnum.AnyMember;

    const personId = currentUser?.personId || "";
    const isRequested = requestedBillingGroups.includes(bg.id);
    const label = isFreeUserAndCanAddSelf
      ? `Join plan`
      : isRequested
      ? `Request sent`
      : `Request to Join`;
    const onClick = () =>
      isFreeUserAndCanAddSelf
        ? onAddMember([personId], bg.id)
        : onRequestToJoinMember(personId, bg.id);
    return (
      <TableCell>
        <Button
          variant="contained"
          startIcon={
            isFreeUserAndCanAddSelf ? (
              <PersonAdd fill={greys.white} />
            ) : (
              <Email fill={greys.white} />
            )
          }
          onClick={onClick}
          disabled={isRequested}
        >
          {label}
        </Button>
      </TableCell>
    );
  };

  const renderBillingGroupExpandToggle = (bg: ISchema.IBillingGroup) => {
    return (
      <TableCell>
        <div className={classes.cell__row}>
          <IconButton onClick={() => handleToggleExpand(bg)}>
            {expandedBillingGroupId === bg.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </div>
      </TableCell>
    );
  };

  const renderSeatCount = (bg: ISchema.IBillingGroup) => {
    const billingGroupSubscription = getSubscriptionFromBillingGroup(bg);

    if (!billingGroupSubscription) {
      return null;
    }
    const isNonStandardSubscription = isSubscriptionNonStandard(
      billingGroupSubscription.subscription,
    );
    const seatCount = billingGroupSubscription
      ? billingGroupSubscription.subscription.seatCount
      : 0;

    const personListCount = getPersonListCountFromBillingGroup(bg) || 0;

    const memberSeatText = isNonStandardSubscription
      ? `${personListCount} paid seats are being used`
      : `${personListCount} of ${seatCount} paid seats are being used`;

    return <span className={classes.settingsText}>{memberSeatText}</span>;
  };

  const renderBillingGroupSettings = (bg: ISchema.IBillingGroup, i: number) => {
    const billingGroupSubscription = getSubscriptionFromBillingGroup(bg);

    const isProductTypePro =
      billingGroupSubscription &&
      isSubscriptionProductTypePro(billingGroupSubscription.subscription);

    const requiresAdmin =
      billingGroupSubscription &&
      (isSubscriptionProductTypeEnterprise(billingGroupSubscription.subscription) ||
        isSubscriptionProductTypeBusiness(billingGroupSubscription.subscription));
    const admins = getAdminsFromBillingGroup(bg) || [];
    const isAdmin = admins.findIndex((a) => a.isYou) >= 0;

    const isSettingsDisabled = (requiresAdmin && !isAdmin) || isProductTypePro;

    return (
      <>
        <TableRow
          key={`billing-group-settings-row-${i}`}
          className={classNames([classes.settingsTableRow, classes.blueBorder])}
        >
          <TableCell colSpan={2}>
            <div className={classes.settingsCell}>
              <div className={classes.settingsHeader}>Subscription settings</div>
              {!isBillingGroupTypeSSO(bg) ? (
                <div className={classes.settingsBody}>
                  <Tooltip
                    title={
                      isSettingsDisabled && !isProductTypePro
                        ? `You don't have permission to manage this plan. To request changes please contact a plan admin.`
                        : isProductTypePro
                        ? `Your current plan doesn't support this provisioning setting. To make a change, please upgrade to a Business plan or contact sales@getclockwise.com`
                        : ""
                    }
                  >
                    <div>
                      <Select
                        disabled={isSettingsDisabled}
                        value={billingGroupInvitePolicy}
                        onChange={(e) =>
                          handleInvitePolicyChange(e.target.value as ISchema.InvitePolicyEnum, bg)
                        }
                      >
                        <MenuItem value={ISchema.InvitePolicyEnum.AnyMember}>Any member</MenuItem>
                        <MenuItem value={ISchema.InvitePolicyEnum.AdminOnly}>Admin only</MenuItem>
                      </Select>
                    </div>
                  </Tooltip>{" "}
                  <span className={classes.settingsText}>
                    can approve users joining the paid plan
                  </span>
                </div>
              ) : (
                <div className={classes.settingsSso}>
                  <SsoAdminPortalButton org={org} billingGroup={bg} disabled={!isAdmin} />
                </div>
              )}
            </div>
          </TableCell>
          <TableCell colSpan={3}>
            <div className={classes.settingsCell}>
              <div className={classes.settingsHeader}>Members</div>
              <div className={classes.settingsBody}>
                {renderSeatCount(bg)}
                <a
                  className={classes.link}
                  href={`${paths.admin}/members/${bg.id}`}
                  onClick={() => {
                    track(
                      TrackingEvents.ADMIN_PANEL
                        .PLANS_AND_BILLING_BILLING_GROUPS_SETTINGS_MANAGE_MEMBERS_CLICK,
                    );
                  }}
                >
                  Manage members <ArrowForward />
                </a>
              </div>
            </div>
          </TableCell>
        </TableRow>
      </>
    );
  };

  const renderBillingGroupRow = (bg: ISchema.IBillingGroup, i: number) => {
    const showBillingGroupSettings = expandedBillingGroupId === bg.id;
    return (
      <>
        <TableRow
          key={`billing-group-row-${i}`}
          className={classNames(showBillingGroupSettings ? [classes.blueBorder] : [])}
        >
          {renderBillingGroupName(bg)}
          {renderBillingGroupPlanTier(bg)}
          {renderBillingGroupAdmins(bg)}
          {!inBillingGroup && !isBillingGroupTypeSSO(bg)
            ? renderBillingGroupRequestToJoin(bg)
            : renderBillingGroupBillingPortal(bg)}
          {renderBillingGroupExpandToggle(bg)}
        </TableRow>
        {showBillingGroupSettings ? renderBillingGroupSettings(bg, i) : <></>}
      </>
    );
  };

  const renderBillingGroupTable = () => {
    return (
      <Table className={classes.table}>
        <TableHead className={classes.table__head}>
          <TableRow>
            <TableCell className={classes.row__subscription}>Subscription</TableCell>
            <TableCell className={classes.row__plan}>Plan tier</TableCell>
            <TableCell className={classes.row__admin}>Admin</TableCell>
            <TableCell className={classes.row__portal}></TableCell>
            <TableCell className={classes.row__expand}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{billingGroupsForOrg.map(renderBillingGroupRow)}</TableBody>
      </Table>
    );
  };

  const renderTeammates = () => {
    const MAX_RENDERED_AVATARS = 8;
    let teammates: ISchema.IOrgPerson[] = [];
    let extraTeammatesCount = 0;

    const currentOrgPersons = getCurrentOrgPersons(org);

    teammates = [...currentOrgPersons].slice(0, MAX_RENDERED_AVATARS);

    extraTeammatesCount =
      currentOrgPersons.length > MAX_RENDERED_AVATARS
        ? currentOrgPersons.length - MAX_RENDERED_AVATARS
        : 0;

    return (
      <div className={classes.row}>
        <AvatarRow
          orgPersons={teammates}
          maxAvatarCount={MAX_RENDERED_AVATARS}
          org={org}
          size={34}
          extraOverflowCount={extraTeammatesCount}
          showTooltip={true}
        />
        <div className={classes.avatarRowDescription}>
          {currentOrgPersons.length} {currentOrgPersons.length === 1 ? "person is" : "people are"}{" "}
          using Clockwise in your organization
        </div>
      </div>
    );
  };

  const renderFreemiumOrgPlan = () => {
    const orgDomain = getDomainOffEmail(org.primaryOrgCalendar);
    const orgAccountingPeriod = getOrgAccountingPeriodFromOrg(org);

    if (!orgAccountingPeriod) {
      return "";
    }

    const percentageUsed = Math.round(
      (orgAccountingPeriod.quotaUsed / orgAccountingPeriod.quotaMax) * 100,
    );
    const formattedCycleRange = getFormattedCycleRangeForOrgAccountingPeriod(orgAccountingPeriod);

    const currentOrgPersons = getCurrentOrgPersons(org);

    return (
      <div style={{ maxWidth: 900 }}>
        <div className={classes.trialContent}>
          <Business className={classes.icon} />
          <span>
            Your organization ({orgDomain}) is on the <span className={classes.bold}>Free</span>{" "}
            plan. Your organization has ({currentOrgPersons.length} active{" "}
            {`user${currentOrgPersons.length === 1 ? "" : "s"}`}) and has used {percentageUsed}% of
            its schedule assists for the current cycle, ({formattedCycleRange}
            ). We recommend upgrading to a Pro plan.
          </span>
        </div>
        <div className={classes.trialButtons}>
          <Button
            color="primary"
            variant="contained"
            classes={{ root: classes.primaryBtn }}
            onClick={onClickViewPlans}
          >
            View Plans
          </Button>
          <Button
            variant="outlined"
            href={salesContactFormUrl}
            target="_blank"
            classes={{ root: classes.secondaryBtn }}
            onClick={onClickContactUs}
          >
            Contact Us
          </Button>
        </div>
      </div>
    );
  };

  const renderNoBgView = () => {
    return (
      <div>
        <div>{renderTeammates()}</div>
        <div>{renderFreemiumOrgPlan()}</div>
      </div>
    );
  };

  return (
    <>
      <Container maxWidth={false}>
        <Title>Plans and Billing</Title>
        {billingGroupsForOrg.length > 0 ? renderBillingGroupTable() : renderNoBgView()}
      </Container>
    </>
  );
};

function mapStateToProps(
  _state: IReduxState,
  _ownProps: IAdminPanelBillingGroupsPlansAndBillingCmpt,
) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(AdminPanelBillingGroupsPlansAndBillingCmpt);

export const AdminPanelBillingGroupsPlansAndBilling = createRefetchContainer(
  reduxContainer,
  adminPanelBillingGroupsPlansAndBillingFragments,
  adminPanelBillingGroupsPlansAndBillingQuery,
);
